import { AdminPanelSettingsOutlined, Link, People, PersonAdd, Search, Send } from '@mui/icons-material'
import { Box, Checkbox, Chip, InputAdornment, Tooltip, useTheme } from '@mui/material'
import { GridActionsCellItem, GridColumns, GridNativeColTypes } from '@mui/x-data-grid-pro'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidateSystemUserCache, useAllUsers, useBulkUserInvite, useCreateSystemInvite } from 'src/api/user'
import { AppTextField } from 'src/components/AppTextField'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { SelectField } from 'src/components/SelectField'
import GenericTable from 'src/components/table/GenericTable'
import { UserStatus } from 'src/enums/user-status'
import { IUser } from 'src/generated-types'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { Padding } from 'src/styles/paddingComponent'
import { coreUtils, getThemeColor } from 'src/utils'
import InviteLink from './InviteLink'

const AllUsers = () => {
  const { t: translate } = useTranslation(['users', 'common'])
  const [searchText, setSearchText] = useState<string>()
  const [status, setStatus] = useState<UserStatus | undefined>()
  const { data, isLoading } = useAllUsers(searchText, status)
  const theme = useTheme()
  const { showConfirmDialog, showDialog } = useDialogCtx()
  const { mutateAsync: inviteUsers } = useCreateSystemInvite()
  const [selectedUsers, setSelectedUsers] = useState<IUser[]>([])
  const { mutateAsync: bulkInviteUsers } = useBulkUserInvite()

  const onInvite = async (email: string, status: UserStatus) => {
    showConfirmDialog(
      translate(status == UserStatus.InComplete ? 'new-invite' : 'renew-invite', { ns: 'user-invite' }),
      translate(status == UserStatus.InComplete ? 'new-invite-message' : 'renew-invite-message', { ns: 'user-invite' }),
      {
        acceptText: translate('yes', { ns: 'common' }),
        cancelText: translate('no', { ns: 'common' }),
      },
      async pop => {
        await inviteUsers({ email })
          .then(() => {
            invalidateSystemUserCache.getSystemUsers()
            toast.success(translate('successfullyInvited', { ns: 'common-api-message' }))
          })
          .catch(() => {
            toast.error(translate('unExpectedError', { ns: 'common-api-message' }))
          })
        pop()
      },
    )
  }

  const copyInviteLink = (email: string, status: UserStatus) => {
    showDialog(InviteLink, {
      componentProps: {
        email: email,
        onResend: () => onInvite(email, status),
      },
      opts: {
        maxWidth: 'md',
        fullWidth: true,
        disableCloseButton: true,
      },
      sx: {
        '& .MuiDialogTitle-root': {
          paddingTop: '16px',
          paddingBottom: '0px',
        },
      },
    })
  }

  const handleSelectCheckbox = useCallback(
    (e, row, data) => {
      const filteredData = [...data]?.filter((item: IUser) => item.status == UserStatus.Pending)
      setSelectedUsers((prev: IUser[]) => {
        const returnDate = coreUtils.handleRowSelectFunc<IUser>(prev, row, filteredData || [], e)
        return returnDate
      })
    },
    [data],
  )

  const handleBulkInvite = useCallback(
    (selectedUsers: IUser[]) => {
      showConfirmDialog(
        translate('new-invite', { ns: 'user-invite' }),
        translate('new-invite-message', { ns: 'user-invite' }),
        {
          acceptText: translate('yes', { ns: 'common' }),
          cancelText: translate('no', { ns: 'common' }),
        },
        async pop => {
          const selectedInviteUsers = selectedUsers.map(user => user.email)
          bulkInviteUsers(
            { emails: selectedInviteUsers },
            {
              onSuccess: () => {
                invalidateSystemUserCache.getSystemUsers()
                setSelectedUsers([])
                toast.success(translate('successfullyUpdated', { ns: 'common-api-message' }))
              },
              onError: error => {
                toast.error(translate('unExpectedError', { ns: 'common-api-message' }))
              },
            },
          )
          pop()
        },
      )
    },
    [translate],
  )

  const columns: GridColumns = useMemo(
    () => [
      {
        field: 'id',
        headerName: '',
        type: 'actions',
        width: 25,
        sx: {
          paddingLeft: '0px',
        },
        renderCell: ({ row }) => {
          return (
            <>
              {/* only users with pending status and associated with at least one client will have the option to select
                  for bulk invite renewal */}
              {row.status == UserStatus.Pending && row.clientNames.length > 0 && (
                <Checkbox
                  sx={{ padding: '0px' }}
                  checked={selectedUsers.some(x => x.id == row.id)}
                  onClick={e => handleSelectCheckbox(e, row, data || [])}
                />
              )}
              {row.isSuperAdmin && <AdminPanelSettingsOutlined color="info" sx={{ ml: 1 }} />}
            </>
          )
        },
      },
      { field: 'firstName', headerName: translate('firstName', { ns: 'users' }), flex: 1 },
      { field: 'lastName', headerName: translate('lastName', { ns: 'users' }), flex: 1 },
      { field: 'email', headerName: translate('email', { ns: 'users' }), flex: 2 },
      { field: 'phoneNumber', headerName: translate('phoneNumber', { ns: 'users' }), flex: 1 },
      {
        field: 'status',
        headerName: translate('status', { ns: 'users' }),
        flex: 1,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ value, row }) => (
          <Box display="flex" justifyContent="center" width="100%">
            <Chip
              label={
                value == UserStatus.Pending
                  ? translate('invited', { ns: 'users' })
                  : translate(UserStatus[value]?.toString(), { ns: 'users' })
              }
              color={value == UserStatus.Pending ? 'warning' : value == UserStatus.Complete ? 'success' : 'error'}
              sx={{ minWidth: 100, justifyContent: 'center' }}
            />
          </Box>
        ),
      },
      {
        field: 'customer',
        headerName: '',
        type: 'actions',
        width: 25,
        renderCell: ({ value, row }) =>
          row.clientNames.length > 0 && (
            <Tooltip
              title={
                <div>
                  {row.clientNames.map((name: string, idx: number) => (
                    <div key={idx}>{name}</div>
                  ))}
                </div>
              }
            >
              <span>
                <People color={row.clientNames.length > 0 ? 'primary' : 'disabled'} sx={{ fontSize: 20 }} />
              </span>
            </Tooltip>
          ),
      },
      {
        field: 'actions',
        type: 'actions' as GridNativeColTypes,
        width: 60,
        getActions: params =>
          // only users with pending status and associated with at least one client will have the option to resend invite and copy invite link
          params.row.status == UserStatus.Pending && params.row.clientNames.length > 0
            ? [
                <GridActionsCellItem
                  key={params.id}
                  label={
                    params.row.status == UserStatus.Pending
                      ? translate('remind-invitation', { ns: 'user-invite' })
                      : translate('invite', { ns: 'common' })
                  }
                  icon={<Send sx={{ fontSize: 24 }} />}
                  onClick={() => onInvite(params.row?.email, params.row?.status)}
                  color="primary"
                  showInMenu
                />,
                <GridActionsCellItem
                  key={`${params.id}-copy-link`}
                  label={translate('copy-invite-link', { ns: 'user-invite' })}
                  icon={<Link sx={{ fontSize: 24 }} />}
                  onClick={() => copyInviteLink(params.row?.email, params.row?.status)}
                  color="primary"
                  showInMenu
                />,
              ]
            : [],
      },
    ],
    [translate, data, selectedUsers],
  )

  return (
    <CommonPageLayout
      titleSection={{
        title: translate('users', { ns: 'users' }),
      }}
      topAction={[
        <SelectField
          key="filter"
          defaultValue={{ label: translate('all', { ns: 'common' }), value: undefined }}
          data={[
            { label: translate('all', { ns: 'common' }), value: undefined },
            { label: translate('invited', { ns: 'users' }), value: UserStatus.Pending },
            { label: translate('completed', { ns: 'users' }), value: UserStatus.Complete },
            { label: translate('InComplete', { ns: 'users' }), value: UserStatus.InComplete },
          ]}
          onChange={(_, v: any) => {
            setSelectedUsers([])
            setStatus(v != null ? (v.value as UserStatus) : undefined)
          }}
          getOptionLabel={o => o.label}
          isOptionEqualToValue={(o, v) => o.value === v.value}
          fullWidth
        />,
        <Padding.ph2 key="search">
          <AppTextField
            label={translate('search', { ns: 'common' })}
            placeholder={translate('search', { ns: 'common' })}
            fullWidth
            sx={{ minWidth: '200px' }}
            onChange={evt => setSearchText(evt.target.value)}
            autoComplete="off"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Padding.ph2>,
        {
          buttonText: translate('invite', { ns: 'user-invite' }),
          buttonIcon: <PersonAdd sx={{ fontSize: 24 }} />,
          onClick: () => handleBulkInvite(selectedUsers),
          disabled: selectedUsers.length === 0,
          variant: 'outlined',
          shadowWeight: null,
          border: 1,
          height: '44px',
          sx: { marginTop: '5px', minWidth: '100px', alignSelf: 'center' },
        },
      ]}
    >
      <Box padding={'8px'} bgcolor={getThemeColor(theme, MUIThemeColors.white)} borderRadius={'4px'}>
        <GenericTable
          id="users-table"
          data={data ?? []}
          getRowHeight={() => 'auto'}
          columns={columns}
          enableToolbar={true}
          loading={isLoading}
          autoHeight={false}
          onRowClick={() => {}} // for enable row cursor pointer
          height={window.innerHeight - 270 + 'px'}
        />
      </Box>
    </CommonPageLayout>
  )
}

export default AllUsers
